/* Public customer-facing mobile ordering page — /order
   No auth. Groups menu by station (with description), lets the customer add
   items + per-item notes, shows a live total, captures phone+name, submits
   as a draft ticket, then polls status: countdown → "visit billing counter"
   → live per-station readiness once the cashier converts. */
import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { API, money } from "../lib/pos";
import { ItemConfigurator } from "../components/ItemConfigurator";

const REFRESH_MS = 4000;

export default function CustomerOrder() {
  const [bundle, setBundle] = useState(null);   // {stations, menu, online_categories}
  const [phase, setPhase] = useState("menu");    // menu → contact → submitting → status
  const [browseMode, setBrowseMode] = useState(() => localStorage.getItem("qoh_browse_mode") || "categories"); // 'categories' | 'stations'
  const [query, setQuery] = useState("");         // global search
  const [openOnlineCat, setOpenOnlineCat] = useState(null);
  const [openStationId, setOpenStationId] = useState(null);
  const [openCategory, setOpenCategory] = useState(null);
  const [configureItem, setConfigureItem] = useState(null);  // menu item currently being configured
  const [cart, setCart] = useState([]);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [orderType, setOrderType] = useState("takeaway"); // 'takeaway' | 'table'
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState(null);      // response from POST /public/drafts
  const [order, setOrder] = useState(null);      // once cashier converts
  const [showTop, setShowTop] = useState(false);

  useEffect(() => {
    const onScroll = () => setShowTop(window.scrollY > 320);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  // Keep --cust-dock-h CSS var in sync with cart-dock height so page bottom padding always clears it
  const dockRef = useRef(null);
  useEffect(() => {
    const el = dockRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const apply = () => document.documentElement.style.setProperty("--cust-dock-h", `${el.offsetHeight}px`);
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => { ro.disconnect(); document.documentElement.style.removeProperty("--cust-dock-h"); };
  }, [cart.length, phase]);

  // Fire QR-scan track once, if reached via ?ref=qr
  useEffect(() => {
    try {
      const p = new URLSearchParams(window.location.search);
      if (p.get("ref") === "qr" && !sessionStorage.getItem("qoh_qr_tracked_order")) {
        sessionStorage.setItem("qoh_qr_tracked_order", "1");
        axios.post(`${API}/qr/track?kind=order&ref=qr`, {}, { withCredentials: false }).catch(() => {});
      }
    } catch (_) { /* ignore */ }
  }, []);

  // Load menu bundle
  useEffect(() => {
    axios.get(`${API}/public/menu-bundle`, { withCredentials: false })
      .then((r) => setBundle(r.data))
      .catch(() => setErr("Menu unavailable. Please try again shortly."));
  }, []);

  // Poll draft after submit
  useEffect(() => {
    if (phase !== "status" || !draft?.draft_number) return;
    let stop = false;
    const tick = async () => {
      try {
        const r = await axios.get(`${API}/public/drafts/${draft.draft_number}`, { withCredentials: false });
        if (stop) return;
        setDraft(r.data);
        if (r.data.status === "converted") setOrder(r.data.order || null);
      } catch (e) {
        if (e.response?.status === 404) {
          setDraft((prev) => ({ ...(prev || {}), status: "expired" }));
        }
      }
    };
    tick();
    const t = setInterval(tick, REFRESH_MS);
    return () => { stop = true; clearInterval(t); };
  }, [phase, draft?.draft_number]);

  if (!bundle) {
    return <div className="cust-page"><div className="cust-loading">Loading menu…</div></div>;
  }
  if (err) {
    return <div className="cust-page"><div className="cust-error">{err}</div></div>;
  }

  const stations = bundle.stations;
  const onlineCats = bundle.online_categories || [];
  const stationNameByCat = new Map();
  for (const s of stations) for (const c of (s.assigned_categories || [])) stationNameByCat.set(c, s.name);
  const menuByCategory = new Map();
  for (const m of bundle.menu) {
    if (!menuByCategory.has(m.category)) menuByCategory.set(m.category, []);
    menuByCategory.get(m.category).push({ ...m, _station: stationNameByCat.get(m.category) || "" });
  }
  // Items are shown price-ascending inside each sub-tile / search list.
  for (const arr of menuByCategory.values()) arr.sort((a, b) => (a.price || 0) - (b.price || 0));

  const q = query.trim().toLowerCase();
  const searchHits = q ? bundle.menu.filter((m) => {
    const st = stationNameByCat.get(m.category) || "";
    return (m.name || "").toLowerCase().includes(q)
      || (m.description || "").toLowerCase().includes(q)
      || (m.category || "").toLowerCase().includes(q)
      || st.toLowerCase().includes(q);
  }).map((m) => ({ ...m, _station: stationNameByCat.get(m.category) || "" }))
    .sort((a, b) => (a.price || 0) - (b.price || 0))
    : [];

  const toggleBrowseMode = (m) => { setBrowseMode(m); localStorage.setItem("qoh_browse_mode", m); setOpenOnlineCat(null); setOpenStationId(null); setOpenCategory(null); setQuery(""); };
  const toggleOnlineCat = (id) => { setOpenOnlineCat((cur) => cur === id ? null : id); setOpenCategory(null); };
  // If manager hasn't created any online categories yet, force station-wise so the menu never appears empty.
  const effectiveMode = (browseMode === "categories" && onlineCats.length === 0) ? "stations" : browseMode;

  const cartTotal = cart.reduce((a, c) => a + c.price * c.quantity, 0);
  const cartCount = cart.reduce((a, c) => a + c.quantity, 0);

  const toggleStation = (sid) => {
    if (openStationId === sid) {
      setOpenStationId(null); setOpenCategory(null);
    } else {
      setOpenStationId(sid); setOpenCategory(null);
    }
  };
  const toggleCategory = (cat) => setOpenCategory((prev) => prev === cat ? null : cat);

  const addToCart = (item) => {
    // If the item has any variants or add-ons, open the configurator.
    // Otherwise add straight to cart.
    const hasChoices = (item.variants || []).length > 0 || (item.addons || []).length > 0;
    if (hasChoices) {
      setConfigureItem(item);
      return;
    }
    setCart((prev) => {
      const idx = prev.findIndex((l) => l.menu_id === item.id && !l.variant && (l.addons || []).length === 0 && !l.note);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], quantity: next[idx].quantity + 1 };
        return next;
      }
      return [...prev, {
        menu_id: item.id, name: item.name, category: item.category, price: item.price, quantity: 1, note: "",
        variant: null, addons: [],
      }];
    });
  };

  const addConfigured = (item, { variant, addons, unitPrice, quantity, note }) => {
    setCart((prev) => [...prev, {
      menu_id: item.id,
      name: item.name,
      category: item.category,
      price: unitPrice,   // final unit price incl. variant + addons
      quantity,
      note: note || "",
      variant: variant || null,
      addons: addons || [],
    }]);
    setConfigureItem(null);
  };
  const decrement = (i) => setCart((prev) => prev.flatMap((l, idx) => {
    if (idx !== i) return [l];
    if (l.quantity <= 1) return [];
    return [{ ...l, quantity: l.quantity - 1 }];
  }));
  const increment = (i) => setCart((prev) => prev.map((l, idx) => idx === i ? { ...l, quantity: l.quantity + 1 } : l));
  const setNote = (i, val) => setCart((prev) => prev.map((l, idx) => idx === i ? { ...l, note: val } : l));
  const removeLine = (i) => setCart((prev) => prev.filter((_, idx) => idx !== i));

  const submitDraft = async () => {
    setBusy(true); setErr("");
    try {
      const r = await axios.post(`${API}/public/drafts`, {
        customer_name: name.trim(),
        customer_phone: phone.trim(),
        items: cart,
        order_type: orderType,
      }, { withCredentials: false });
      setDraft(r.data);
      setPhase("status");
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not place your order. Please try again.");
    } finally { setBusy(false); }
  };

  if (phase === "status") return (
    <StatusView draft={draft} order={order} onNew={() => {
      setCart([]); setDraft(null); setOrder(null); setPhase("menu");
    }} />
  );

  if (phase === "contact") return (
    <div className="cust-page">
      <div className="cust-hdr">
        <img src="/logo.jpg" alt="" className="cust-logo" />
        <div><b>Shimla QoH Food Court</b><small>Almost done · {cartCount} items · {money(cartTotal)}</small></div>
      </div>
      <div className="cust-body">
        <h2 style={{ marginTop: 8 }}>Your details</h2>
        <p className="cust-muted">We'll use your phone to identify the pickup. We won't send you marketing messages.</p>

        <div className="cust-otype" data-testid="cust-order-type">
          <button
            data-testid="cust-otype-takeaway"
            className={`cust-otype-btn ${orderType === "takeaway" ? "on" : ""}`}
            onClick={() => setOrderType("takeaway")}
          ><b>Takeaway</b><small>Pick up from the counter</small></button>
          <button
            data-testid="cust-otype-dinein"
            className={`cust-otype-btn ${orderType === "table" ? "on" : ""}`}
            onClick={() => setOrderType("table")}
          ><b>Dine-in</b><small>Eat at the food court</small></button>
        </div>

        <label className="cust-field">Name
          <input data-testid="cust-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" autoComplete="off" />
        </label>
        <label className="cust-field">Phone number
          <input data-testid="cust-phone" inputMode="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="10-digit mobile" autoComplete="off" />
        </label>
        {err && <div className="cust-error" data-testid="cust-error">{err}</div>}
        <button data-testid="cust-submit" className="cust-primary" disabled={busy || !name.trim() || phone.trim().length < 6} onClick={submitDraft}>
          {busy ? "Placing…" : `Place draft · ${money(cartTotal)}`}
        </button>
        <button className="cust-secondary" onClick={() => setPhase("menu")}>← Back to menu</button>
      </div>
      <CustFooter />
    </div>
  );

  // menu phase
  return (
    <div className="cust-page">
      {configureItem && (
        <ItemConfigurator
          item={configureItem}
          onCancel={() => setConfigureItem(null)}
          onAdd={(sel) => addConfigured(configureItem, sel)}
        />
      )}
      <div className="cust-hdr">
        <img src="/logo.jpg" alt="" className="cust-logo" />
        <div><b>Shimla QoH Food Court</b><small>{effectiveMode === "categories" ? "Pick a category, add to cart, checkout" : "Tap a station, add to cart, checkout"}</small></div>
      </div>

      <div className="cust-search-row" data-testid="cust-search-row">
        <input
          data-testid="cust-search"
          className="cust-search"
          type="search"
          placeholder="Search dishes, category, station…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        {query && (
          <button className="cust-search-clear" onClick={() => setQuery("")} data-testid="cust-search-clear">×</button>
        )}
      </div>

      {!q && onlineCats.length > 0 && (
        <div className="cust-mode-switch" role="tablist" data-testid="cust-mode-switch">
          <button
            role="tab"
            data-testid="cust-mode-categories"
            className={`cust-mode-btn ${effectiveMode === "categories" ? "on" : ""}`}
            onClick={() => toggleBrowseMode("categories")}
          >Browse by category</button>
          <button
            role="tab"
            data-testid="cust-mode-stations"
            className={`cust-mode-btn ${effectiveMode === "stations" ? "on" : ""}`}
            onClick={() => toggleBrowseMode("stations")}
          >Browse station-wise</button>
        </div>
      )}

      {q ? (
        <div className="cust-search-results" data-testid="cust-search-results">
          <p className="cust-search-summary">{searchHits.length} match{searchHits.length === 1 ? "" : "es"} for “{query}”</p>
          <div className="cust-menu-list">
            {searchHits.length === 0 && <p className="cust-muted" style={{ padding: 24, textAlign: "center" }}>No dishes matched your search. Try another word.</p>}
            {searchHits.map((m) => (
              <div key={m.id} className="cust-menu-row" data-testid={`cust-item-${m.id}`}>
                <div className="cust-menu-info">
                  <b>{m.name}</b>
                  {m.description ? <small>{m.description}</small> : null}
                  <em>{m._station || m.category}</em>
                </div>
                <div className="cust-menu-side">
                  <span>{money(m.price)}</span>
                  <button className="cust-add" onClick={() => addToCart(m)} data-testid={`cust-add-${m.id}`}>+ Add</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : effectiveMode === "categories" ? (
        <CategoriesView
          onlineCats={onlineCats}
          openOnlineCat={openOnlineCat}
          toggleOnlineCat={toggleOnlineCat}
          openCategory={openCategory}
          toggleCategory={toggleCategory}
          menuByCategory={menuByCategory}
          stationNameByCat={stationNameByCat}
          addToCart={addToCart}
        />
      ) : (
      <div className="cust-accordion" data-testid="cust-station-accordion">
        {stations.map((s) => {
          const isOpen = openStationId === s.id;
          return (
            <div key={s.id} className={`cust-station-block ${isOpen ? "open" : ""}`}>
              <button
                data-testid={`cust-station-btn-${s.id}`}
                className="cust-station-head"
                onClick={() => toggleStation(s.id)}
              >
                <div className="cust-station-head-txt">
                  <b>{s.name}</b>
                  {s.description ? <small>{s.description}</small> : (s.assigned_categories || []).length ? <small>{s.assigned_categories.join(" · ")}</small> : null}
                </div>
                <span className="cust-chev">{isOpen ? "▾" : "▸"}</span>
              </button>
              {isOpen && (
                <div className="cust-cat-list" data-testid={`cust-cat-list-${s.id}`}>
                  {(s.assigned_categories || []).map((cat) => {
                    const items = menuByCategory.get(cat) || [];
                    if (!items.length) return null;
                    const catOpen = openCategory === cat;
                    return (
                      <div key={cat} className={`cust-cat-block ${catOpen ? "open" : ""}`}>
                        <button
                          data-testid={`cust-cat-btn-${cat.replace(/\s+/g, "-")}`}
                          className="cust-cat-head"
                          onClick={() => toggleCategory(cat)}
                        >
                          <b>{cat}</b>
                          <em>{items.length} item{items.length === 1 ? "" : "s"}</em>
                          <span className="cust-chev">{catOpen ? "▾" : "▸"}</span>
                        </button>
                        {catOpen && (
                          <div className="cust-menu-list" data-testid={`cust-item-list-${cat.replace(/\s+/g, "-")}`}>
                            {items.map((m) => {
                              const hasChoices = (m.variants || []).length > 0 || (m.addons || []).length > 0;
                              return (
                                <div key={m.id} className="cust-menu-row" data-testid={`cust-item-${m.id}`}>
                                  <div className="cust-menu-info">
                                    <b>{m.name}</b>
                                    {m.description ? <small>{m.description}</small> : null}
                                  </div>
                                  <div className="cust-menu-side">
                                    <span>{money(m.price)}</span>
                                    <button className="cust-add" onClick={() => addToCart(m)} data-testid={`cust-add-${m.id}`}>
                                      + Add
                                      {hasChoices && <em className="cust-add-hint">Customise</em>}
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
      )}

      <div className="cust-cart-dock" data-testid="cust-cart-dock" ref={dockRef}>
        <CartInline cart={cart} money={money} increment={increment} decrement={decrement} setNote={setNote} removeLine={removeLine} />
        <div className="cust-cart-total">
          <b>Total <span>{money(cartTotal)}</span></b>
          <button
            data-testid="cust-checkout"
            className="cust-primary"
            disabled={!cart.length}
            onClick={() => setPhase("contact")}
          >{cart.length ? `Checkout · ${cartCount} item${cartCount === 1 ? "" : "s"}` : "Cart is empty"}</button>
        </div>
      </div>
      <CustFooter />
      {showTop && (
        <button
          type="button"
          className="cust-scroll-top"
          onClick={scrollToTop}
          data-testid="cust-scroll-top"
          aria-label="Back to top"
          title="Back to top"
        >↑</button>
      )}
    </div>
  );
}

function CategoriesView({ onlineCats, openOnlineCat, toggleOnlineCat, openCategory, toggleCategory, menuByCategory, stationNameByCat, addToCart }) {
  const panelRef = useRef(null);
  useEffect(() => {
    if (openOnlineCat && panelRef.current) {
      // Small delay so the DOM has painted the panel before scrolling.
      const t = setTimeout(() => {
        try { panelRef.current.scrollIntoView({ behavior: "smooth", block: "start" }); } catch (_) { /* ignore */ }
      }, 30);
      return () => clearTimeout(t);
    }
  }, [openOnlineCat]);
  if (!onlineCats.length) {
    return (
      <div className="cust-body">
        <p className="cust-muted" style={{ textAlign: "center", padding: 22 }}>
          No online categories set up yet. Switch to “Browse station-wise”.
        </p>
      </div>
    );
  }
  return (
    <div className="cust-oc-wrap" data-testid="cust-oc-grid">
      <div className="cust-oc-grid">
        {onlineCats.map((oc) => {
          const isOpen = openOnlineCat === oc.id;
          const subCats = (oc.categories || []).filter((cat) => (menuByCategory.get(cat) || []).length > 0);
          const itemCount = subCats.reduce((n, c) => n + (menuByCategory.get(c) || []).length, 0);
          return (
            <button
              key={oc.id}
              type="button"
              className={`cust-oc-tile ${isOpen ? "on" : ""}`}
              onClick={() => toggleOnlineCat(oc.id)}
              data-testid={`cust-oc-tile-${oc.id}`}
            >
              <b>{oc.name}</b>
              <small>{itemCount} dish{itemCount === 1 ? "" : "es"}</small>
            </button>
          );
        })}
      </div>
      {openOnlineCat && (() => {
        const oc = onlineCats.find((x) => x.id === openOnlineCat);
        if (!oc) return null;
        const subCats = (oc.categories || []).filter((cat) => (menuByCategory.get(cat) || []).length > 0);
        // Flatten items from every assigned category into one price-ascending list.
        const flatItems = subCats
          .flatMap((cat) => menuByCategory.get(cat) || [])
          .sort((a, b) => (a.price || 0) - (b.price || 0));
        return (
          <div className="cust-oc-panel" ref={panelRef} data-testid={`cust-oc-panel-${oc.id}`}>
            <div className="cust-oc-panel-hdr"><b>{oc.name}</b><small>{flatItems.length} dish{flatItems.length === 1 ? "" : "es"} · low to high price</small></div>
            <div className="cust-menu-list" data-testid={`cust-oc-item-list-${oc.id}`}>
              {flatItems.map((m) => {
                const hasChoices = (m.variants || []).length > 0 || (m.addons || []).length > 0;
                return (
                  <div key={m.id} className="cust-menu-row" data-testid={`cust-item-${m.id}`}>
                    <div className="cust-menu-info">
                      <b>{m.name}</b>
                      {m.description ? <small>{m.description}</small> : null}
                    </div>
                    <div className="cust-menu-side">
                      <span>{money(m.price)}</span>
                      <button className="cust-add" onClick={() => addToCart(m)} data-testid={`cust-add-${m.id}`}>
                        + Add
                        {hasChoices && <em className="cust-add-hint">Customise</em>}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}
    </div>
  );
}

function CustFooter() {
  return (
    <footer className="cust-footer" data-testid="cust-footer">
      <a
        href="https://shimlafoodcourt.com"
        target="_blank"
        rel="noopener noreferrer"
        data-testid="cust-footer-link"
      >Shimla QoH Food Court, Near Lift, Shimla</a>
      <small>Copyright {new Date().getFullYear()} · KKK Food Court Pvt Ltd</small>
    </footer>
  );
}

function CartInline({ cart, money, increment, decrement, setNote, removeLine }) {
  if (!cart.length) return null;
  const summariseChoices = (l) => {
    const parts = [];
    if (l.variant?.name) parts.push(l.variant.name);
    if ((l.variant?.sub_variants || []).length) {
      parts.push((l.variant.sub_variants || []).map((sv) => sv.name).join(", "));
    }
    (l.addons || []).forEach((a) => parts.push(`+${a.quantity}× ${a.name}`));
    return parts.filter(Boolean).join(" · ");
  };
  return (
    <div className="cust-cart-lines">
      {cart.map((l, i) => {
        const choicesText = summariseChoices(l);
        return (
          <div key={i} className="cust-cart-line" data-testid={`cust-cart-line-${i}`}>
            <div className="cust-cart-head">
              <b>{l.name}</b>
              <span>{money(l.price * l.quantity)}</span>
              <button className="cust-x" onClick={() => removeLine(i)} title="Remove">×</button>
            </div>
            {choicesText && (
              <div className="cust-cart-choices" data-testid={`cust-cart-choices-${i}`}>{choicesText}</div>
            )}
            <div className="cust-cart-controls">
              <button onClick={() => decrement(i)}>−</button>
              <em>{l.quantity}</em>
              <button onClick={() => increment(i)}>+</button>
              <input
                placeholder="Notes (e.g. no onions)"
                value={l.note || ""}
                onChange={(e) => setNote(i, e.target.value)}
                data-testid={`cust-note-${i}`}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function StatusView({ draft, order, onNew }) {
  // Countdown until expiry — only while awaiting pickup.
  const expiresAt = draft?.expires_at ? new Date(draft.expires_at).getTime() : null;
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const secsLeft = expiresAt ? Math.max(0, Math.floor((expiresAt - now) / 1000)) : 0;
  const mm = String(Math.floor(secsLeft / 60)).padStart(2, "0");
  const ss = String(secsLeft % 60).padStart(2, "0");

  if (!draft) return <div className="cust-page"><div className="cust-loading">Checking status…</div></div>;

  if (draft.status === "expired" || (draft.status === "awaiting_pickup" && secsLeft <= 0 && expiresAt)) {
    return (
      <div className="cust-page">
        <div className="cust-hdr"><img src="/logo.jpg" alt="" className="cust-logo" /><div><b>Draft expired</b></div></div>
        <div className="cust-body" style={{ textAlign: "center" }}>
          <div className="cust-icon">✕</div>
          <h2>Your draft expired</h2>
          <p className="cust-muted">Drafts are only valid for 10 minutes. Please place a new one.</p>
          <button className="cust-primary" onClick={onNew} data-testid="cust-new-order">Start a new order</button>
        </div>
        <CustFooter />
      </div>
    );
  }

  // Converted → live order status
  if (draft.status === "converted" && order) {
    const stationReady = order.station_ready || {};
    // Figure out which stations are involved (by item category → station lookup).
    // We stashed stations in the bundle at menu load — but at status stage the
    // bundle is gone. Fall back to grouping items by category as a station label.
    // Server also gives us the readiness map keyed by station_id so we still know
    // which stations are done; we display readiness by category by matching category
    // → the first ready station key. Good enough for the customer view.
    const categoriesInvolved = [...new Set((order.items || []).map((i) => i.category))];
    const anyReadyKey = Object.keys(stationReady)[0];
    const totalStations = categoriesInvolved.length;
    const readyStations = Object.keys(stationReady).length;
    return (
      <div className="cust-page">
        <div className="cust-hdr"><img src="/logo.jpg" alt="" className="cust-logo" /><div><b>Order placed</b><small>Token {order.token_number} · #{order.order_number}</small></div></div>
        <div className="cust-body">
          <div className="cust-status-hero">
            <div className="cust-status-token" data-testid="cust-status-token">T{String(order.token_number).padStart(2, "0")}</div>
            <div>
              <b>Status: {(order.status || "sent").toUpperCase()}</b>
              <small>{money(order.total || 0)} · {readyStations}/{totalStations} stations ready</small>
            </div>
          </div>
          <h3 style={{ marginTop: 22 }}>Kitchen progress</h3>
          <div className="cust-station-progress" data-testid="cust-station-progress">
            {categoriesInvolved.map((cat, idx) => {
              const ready = readyStations >= (idx + 1) || readyStations === totalStations;
              return (
                <div key={cat} className={`cust-station-row ${ready ? "ready" : "cooking"}`}>
                  <b>{cat}</b>
                  <span>{ready ? "✓ Ready" : "Cooking…"}</span>
                </div>
              );
            })}
          </div>
          {order.status === "served" || order.status === "paid" ? (
            <div className="cust-station-row ready" style={{ marginTop: 14, borderRadius: 8, textAlign: "center", justifyContent: "center" }}>
              <b>🎉 Your order is ready!</b>
            </div>
          ) : null}
          <h3 style={{ marginTop: 22 }}>Your items</h3>
          <div className="cust-order-items">
            {(order.items || []).map((it, i) => (
              <div key={i} className="cust-order-item">
                <b>{it.quantity}×</b> {it.name}
                {it.note ? <small>Note: {it.note}</small> : null}
              </div>
            ))}
          </div>
        </div>
        <CustFooter />
      </div>
    );
  }

  // Awaiting pickup — show the draft number + countdown + instruction
  return (
    <div className="cust-page">
      <div className="cust-hdr"><img src="/logo.jpg" alt="" className="cust-logo" /><div><b>Almost there!</b><small>Draft ticket · awaiting pickup</small></div></div>
      <div className="cust-body" style={{ textAlign: "center" }}>
        <div className="cust-billing-callout" data-testid="cust-billing-callout">
          <div className="cust-billing-icon" aria-hidden="true">🏷️</div>
          <p className="cust-billing-headline">
            Please visit the <b>billing counter</b> to place the order and <b>quote this ticket ID</b>
          </p>
          <div className="cust-billing-id" data-testid="cust-draft-number">
            <small>Ticket ID</small>
            <b>#{draft.draft_number}</b>
          </div>
        </div>
        <div className="cust-countdown" data-testid="cust-countdown">
          Expires in <b>{mm}:{ss}</b>
        </div>
        <div className="cust-order-items" style={{ marginTop: 18, textAlign: "left" }}>
          {(draft.items || []).map((it, i) => (
            <div key={i} className="cust-order-item">
              <b>{it.quantity}×</b> {it.name}
              {it.note ? <small>Note: {it.note}</small> : null}
            </div>
          ))}
        </div>
      </div>
      <CustFooter />
    </div>
  );
}
